import { IMentionable, IMentionsState } from 'common/types/mentions.types'
import {
  MutableRefObject,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import ContentEditable from 'react-contenteditable'
import styled from 'styled-components'
import { INPUT_PLACEHOLDER } from 'common/types/message.types'
import { getHTMLTextLength } from 'common/utils/getHTMLTextLength'
import { ITagSearchItem } from 'common/types/hashtags.types'
import { findHashtag } from 'common/utils/findHashtag'

const StyledMentionableTextField = styled(ContentEditable) <{
  singleLine?: boolean
  width?: string
  scrollBarColor?: string
}>`
  && {
    max-height: 140px;
    width: ${props => (props?.width ? props.width : '80%')};
    outline: none;
    overflow-y: auto;
    color: ${props => props.theme.colors.accent1};
    position: relative;
    cursor: text;
    min-height: 20px;
    font-size: 1rem;
  }

  :focus {
    outline: none !important;
  }

  ${props =>
    props.singleLine
      ? `
      white-space: nowrap;
      overflow-x: hidden;
    `
      : ``}

  ::-webkit-scrollbar {
    width: 0.43em;
    background-color: transparent;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 0.3125em;
    background-color: ${props => (props?.scrollBarColor ? props.scrollBarColor : props.theme.colors.accent)};
    outline: 1px solid transparent;
  }

  ::-webkit-scrollbar:horizontal {
    display: none;
  }

  ::-webkit-scrollbar-corner{
    color: transparent;
  }

  ::before {
    position: absolute;
    top: 0;
    left: 0;
    content: '${props => props.placeholder ?? INPUT_PLACEHOLDER}';
    color: ${props => props.theme.colors.accent};
    font-size: 0.86rem;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  :focus::before,
  &&:not(:empty)::before {
    content: '';
  }

  svg.hashtag-icon path,
  a {
    color: ${props => props.theme.colors.primary};
    fill: ${props => props.theme.colors.primary} !important;
  }

  svg {
    width: calc(0.5625rem);
    height: calc(1rem);
    transform: translateY(25%);
  }
`

export interface IMentionsTextfieldProps {
  theme: any
  users: IMentionable[]
  resetMentioningRef: MutableRefObject<Function>
  triggerFeatureRef?: MutableRefObject<Function>
  maxLength: number
  totalSuggestions: number
  mentionedContainerClass: string
  typingID: string
  setMentions: Function
  getResults: Function
  setText: Function
  setUsers: Function
  mentionedCounter?: number
  width?: string
  defaultValue?: string
  placeholder?: string
  height?: string
  edit?: boolean
  singleLine?: boolean
  isMentioning?: boolean
  fullWidth?: boolean
  setIsMentioning?: Function
  setCursorEl?: Function
  setMentionedCounter?: Function
  setCharacterCount: (newVal: number) => void
  mentioningActive?: boolean
  mentions?: Map<string, IMentionable>
  hasTagActive?: boolean
  isTagging?: boolean
  setIsTagging?: Function
  setTagResult?: Function
  setHashCount?: Function
  hashCount?: number
  tags?: Map<string, ITagSearchItem>
  setAutoTag?: Function
  autoTag?: boolean
  setSelectedTarget?: Function
  selectedTarget?: string
  state?: IMentionsState
  stateDispatch?: (value: Partial<IMentionsState>) => void
}

const useStyles = (props: IMentionsTextfieldProps) => {
  const { fullWidth, height, singleLine } = props

  return useMemo(
    () => ({
      textfield: {
        width: fullWidth ? '100% !important' : null,
        height,
        border: 'none',
        overflow: singleLine ? 'visible' : 'auto',
        overflowX: singleLine ? 'hidden' : 'unset',
      }
    }),
    [props]
  )
}

export const MentionsTextfield = (props: PropsWithChildren<IMentionsTextfieldProps>) => {
  const {
    setUsers,
    maxLength,
    typingID,
    singleLine,
    getResults,
    setText,
    setCursorEl,
    defaultValue,
    setMentionedCounter,
    mentionedCounter,
    resetMentioningRef,
    placeholder,
    isMentioning,
    setIsMentioning,
    width,
    mentioningActive,
    hasTagActive,
    isTagging,
    setIsTagging,
    setTagResult,
    setHashCount,
    state,
    stateDispatch
  } = props

  const isActive = mentioningActive ?? true

  const styles = useStyles(props)

  const resetMentioning = (resetMentioningRef.current = () => {
    setMentionedCounter(0)
    setHashCount?.(0)
    setIsMentioning(false)
    setUsers?.([])
    setTagResult?.([])
    setIsTagging?.(false)
  })

  const triggerFeature = ({ isAsperand, isHashTag, value, shouldSetTextfieldValue: _shouldSetTextfieldValue }) => {
    const shouldSetTextfieldValue = _shouldSetTextfieldValue ?? true
    const triggerChar = isHashTag ? '#' : '@'

    setIsMentioning(true)
    setMentionedCounter(0)
    isAsperand && getResults('', false)
    isHashTag && getResults('', false, false)
    setIsTagging?.(isHashTag)
    shouldSetTextfieldValue && setText(value)

    const selection = window.getSelection()
    const currentRange = selection.getRangeAt(0)
    const range = document.createRange()

    const element = ref?.current

    const startElement = currentRange.startContainer
    const startOffset = currentRange.startOffset

    range.setStart(element.firstChild, 0)
    range.setEnd(startElement, startOffset)

    const hashCount = Math.max(
      0,
      range
        ?.toString()
        ?.split('')
        ?.filter(val => val === triggerChar).length
    )

    const triggerIndex = findHashtag(hashCount, value, triggerChar)

    stateDispatch({ triggerIndex, hashCount })
  }

  const ref = useRef<HTMLDivElement>()

  useEffect(() => {
    if (ref.current) {
      ref.current.onpaste = event => {
        event.preventDefault()

        // @ts-ignore
        const text = (event.clipboardData || window.clipboardData).getData('text')
        const selection = window.getSelection()

        if (!selection.rangeCount) {
          return false
        }

        selection.deleteFromDocument()
        const range = selection.getRangeAt(0)

        const textNode = document.createTextNode(text)

        range.insertNode(textNode)
        range.collapse(false)

        setText(ref.current.innerHTML)
      }
    }
  }, [ref])

  useEffect(() => {
    if (defaultValue?.length) {
      if (!getHTMLTextLength(defaultValue)) {
        setText('')
        resetMentioningRef.current?.()
      }
    }
  }, [defaultValue])

  return (
    <>
      <StyledMentionableTextField
        style={styles.textfield}
        width={width}
        disabled={false}
        innerRef={ref}
        onChange={evt => {
          let value = evt?.currentTarget?.innerHTML || ''
          const textValue = evt?.currentTarget?.innerText || ''
          const currentTarget = evt?.currentTarget

          if (isActive) {
            const nativeEvent: any = evt.nativeEvent
            // @ts-ignore
            const isBackSpace = nativeEvent.inputType === 'deleteContentBackward'
            const isAsperand = nativeEvent.data === '@'
            const isHashTag = nativeEvent.data === '#'

            const hasTriggered = isAsperand || (hasTagActive && isHashTag)

            if (!isMentioning && hasTriggered) {
              triggerFeature({
                isAsperand,
                isHashTag,
                value,
                shouldSetTextfieldValue: true
              })

              return
            }

            if (isMentioning) {
              const triggerChar = isTagging ? '#' : '@'
              const isAlphaNumeric = new RegExp(/[^a-zA-Z0-9]/g)
              const isSpecialUntriggerChar = isAlphaNumeric.test(nativeEvent.data)

              if (isTagging && (isSpecialUntriggerChar || !nativeEvent?.data) && !isBackSpace) {
                setText(value)
                resetMentioning()
                return
              }

              if (isTagging && triggerChar != value[state?.triggerIndex]) {
                setText(value)
                resetMentioning()
                return
              }

              if (isAsperand) {
                isAsperand && setText(value.replace('@', ''))
                resetMentioning()
                triggerFeature({
                  isAsperand,
                  isHashTag,
                  value,
                  shouldSetTextfieldValue: true
                })

                return
              }

              if (!isTagging && nativeEvent?.data === '@') {
                setText(value.replace('@', ''))
                resetMentioning()
                return
              }

              if (value.includes(triggerChar)) {
                const { triggerIndex } = state
                let newMentionedCounter = 0

                if (isBackSpace) {
                  newMentionedCounter = Math.max(0, mentionedCounter - 1)
                } else if (hasTriggered) {
                  newMentionedCounter = 0
                } else {
                  newMentionedCounter = mentionedCounter + 1
                }

                setMentionedCounter(newMentionedCounter)

                const triggerCharPos = triggerIndex + 1
                const query: string = value.slice(triggerCharPos, triggerCharPos + newMentionedCounter)

                !isTagging && getResults(query.replace('@', ''), false)
                isTagging && getResults(query.replace('#', '').toLowerCase(), false, false)
              } else {
                resetMentioning()
              }
            }
          }

          setText(evt?.currentTarget?.innerHTML)
        }}
        onKeyUp={event => {
          const target = event?.target as HTMLDivElement
          const value = target?.innerHTML || ""

          if (!getHTMLTextLength(value)) {
            resetMentioning()
            setText('')
          }
        }}
        placeholder={placeholder}
        singleLine={singleLine}
        html={defaultValue}
        className={typingID}
        onFocus={() => {
          setCursorEl(true)
        }}
        onKeyDown={event => {
          const target = event.currentTarget
          const value = target?.innerText || ''

          if (singleLine) {
            if (event.keyCode === 13) {
              event.preventDefault()
              return
            }
          }

          if (value.length >= maxLength) {
            if (event.keyCode !== 8) {
              event.preventDefault()
            }
          }

          if (isMentioning && event.key === '@') {
            event.preventDefault()
            return
          }
        }}
      />
    </>
  )
}
